import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from '../../utils/dayjs';
import { ToastrService } from 'ngx-toastr';
// @ts-ignore
import qrIous from 'qrious';
import { Subscription } from 'rxjs';

import { HeaderComponent } from '../../components/header/header.component';
import { IconComponent } from '../../components/icon/icon.component';
import { TitleComponent } from '../../components/title/title.component';
import { CurrencyPipe } from '../../pipes/currency.pipe';
import { Table } from '../../domain/models/table';
import { PaymentService } from '../../domain/services/payment.service';
import { TableService } from '../../domain/services/table.service';
import { PaymentDetails } from '../../services/payments/dto/payment-details-response-dto';
import { LocalStorageService } from '../../services/local-storage.service';
import { ConfigService } from '../../services/config.service';
import { SocketService } from '../../services/socket/socket.service';

const TIME_IN_SECONDS = 600;//10 minutos
@Component({
    selector: 'app-payment',
    imports: [
        CommonModule,
        IconComponent,
        HeaderComponent,
        TitleComponent,
        CurrencyPipe,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './payment.component.html',
    styleUrl: './payment.component.scss'
})
export class PaymentComponent {

  intentUuid: string;
  qrCode: string;
  uuid: string;
  timer: number;
  ticking: boolean = false;
  timeLabel: string = '10:00'
  timezone: string = 'America/Sao_Paulo';
  watchIntentInterval: any;
  tableSubscription: Subscription;
  table: Table;

  constructor(
    private tableService: TableService,
    private paymentService: PaymentService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private localStorageService: LocalStorageService,
    private configService: ConfigService,
    private socketService: SocketService,
  ) {
    this.uuid = String(this.route.snapshot.paramMap.get('uuid'));

    this.table = this.tableService.getTable();

    if(!this.table) {
      this.tableService.loadTable(this.uuid)
    }

    if(!this.tableSubscription) {
      this.tableSubscription = this.tableService.$openedTable.subscribe((table: Table | null) => {
        if(table) {
          this.table = table;
        }
      })
    }

    const intent = this.paymentService.getIntent();

    if(!intent) {
      this.router.navigate([`${this.uuid}/`])
    }else{
      this.intentUuid = intent.uuid;
      this.qrCode = intent.qrCode ?? '';
      intent.calculateEndDate();
      this.timer = intent.getDiff();

      if(this.timer <= 0) {
        this.timeLabel = '0:00';
      }
      this.tickSmsTimer();
      this.watchIntent();

      if (this.showQrCode && this.qrCode) {
        this.generateQr(this.qrCode);
      }
    }
  }

  get showQrCode(): boolean {
    return this.configService.isLandscapeTabletEnabled();
  }

  get amountToPay(): number {
    return this.paymentService.getIntentTotal();
  }

  private generateQr(value: string): void {
    setTimeout(() => {
      const canvasElement = document.getElementById('pix-qrcode');
      if (canvasElement) {
        new qrIous({ element: canvasElement, value, size: 240 });
      }
    }, 50);
  }

  handleClose(): void {
    // No modo garcom/landscape o pagamento e direto da home (sem etapa de "ver valor"),
    // entao sair/voltar deve retornar para a home, nao para /details.
    const destination = this.configService.isLandscapeTabletEnabled()
      ? `${this.uuid}`
      : `${this.uuid}/details`;

    // Cancela o pagamento pendente no backend antes de sair
    if (this.intentUuid) {
      this.paymentService.cancelPayment(this.intentUuid).subscribe({
        next: () => {
          console.log('Pagamento cancelado com sucesso');
        },
        error: (err) => {
          console.error('Erro ao cancelar pagamento:', err);
        },
        complete: () => {
          this.router.navigate([destination]);
        }
      });
    } else {
      this.paymentService.clearIntent();
      this.router.navigate([destination]);
    }

    // Limpa o intervalo de verificação
    if (this.watchIntentInterval) {
      clearInterval(this.watchIntentInterval);
    }
  }

  tickSmsTimer() {
    if(this.timer > 0 && !this.ticking) {
      this.ticking = true

      setTimeout(() => {
        this.timer--;
        this.ticking = false;
        this.tickSmsTimer();
        this.formatTimeLabel();
      }, 1000)
    } else {
      this.ticking = false
    }
  }

  formatTimeLabel(){
    this.timeLabel = dayjs().tz(this.timezone)
      .set('minute', 10)
      .set('second', 0)
      .subtract(TIME_IN_SECONDS - this.timer, 'second')
      .format('mm:ss')
  }

  watchIntent() {
    if(!this.watchIntentInterval) {
      this.watchIntentInterval = setInterval(() => {
        this.getIntent()
      }, 4000)
    }
  }

  getIntent() {
    this.paymentService.getDetails(this.intentUuid).subscribe((res: PaymentDetails) => {
      if(res) {
        if(res.status == 'CANCELLED' || res.status == 'PAID') {
          this.paymentService.setIntentStatus(res.status);
          this.localStorageService.removeItem('intent');

          if(this.watchIntentInterval) {
            clearInterval(this.watchIntentInterval);
          }
        }

        if (res.status == 'PAID') {
          this.socketService.emit(
            'mesa:update',
            JSON.stringify({
              uuid: crypto.randomUUID(),
            }),
          );
          this.router.navigate([`${this.uuid}/payment-finished`]);
        }

        if (res.status == 'CANCELLED') {
          this.router.navigate([`${this.uuid}/`]);
        }
      }
    })
  }

  handleCopyQrCode(): void {
    navigator.clipboard.writeText(this.qrCode).then(() => {
      this.toastr.success('Pix copia e cola copiado!')
    }).catch(err => {
      console.error('Erro ao copiar texto: ', err);
      this.handleCopyFallback();
    });
  }

  handleCopyFallback() {
    const textarea = document.createElement('textarea');
    textarea.value = this.qrCode;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      this.toastr.success('Pix copia e cola copiado!')
    } catch (err) {
      console.error('Erro ao copiar texto fallback: ', err);
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
